import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Pill } from "../hs/hsUtils";
import { TONE_CLASSES } from "../assets/assetUtils";
import { AuthImage, Stars, areaText, availText, rateText } from "./mpUtils";
import { Heart, Lightning, SealCheck, MapPin, Clock, ChatCircleDots, Airplane } from "@phosphor-icons/react";
import { fmt } from "./mpUtils";

export const AWAY_TONE = "text-sky-800 bg-sky-50 border-sky-200 dark:text-sky-300 dark:bg-sky-950/30 dark:border-sky-800";
export function awayText(a) {
  if (!a) return "";
  if (a.away) return `Away until ${fmt(a.until)}`;
  if (a.soon) return `Away ${fmt(a.from)} – ${fmt(a.until)}`;
  return "";
}

/** One contractor in the browse grid. */
export function ListingCard({ c, onFavourite, compare, onCompare, compareDisabled }) {
  const away = awayText(c.away);
  return (
    <div className={`rounded-xl border bg-card overflow-hidden flex flex-col group transition-colors ${compare ? "border-primary ring-1 ring-primary/40" : "border-border hover:border-primary/50"}`} data-testid={`listing-card-${c.id}`}>
      <Link to={`/marketplace/contractors/${c.id}`} className="block">
        {c.photos?.length ? <AuthImage url={c.photos[0].url} alt={c.photos[0].name} className="h-36 w-full object-cover" /> : <div className="h-36 w-full bg-gradient-to-br from-muted to-muted/40 flex items-center justify-center text-muted-foreground text-xs">No photos yet</div>}
      </Link>
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex items-start gap-2">
          <Link to={`/marketplace/contractors/${c.id}`} className="font-semibold leading-tight hover:underline flex-1 min-w-0 truncate" data-testid={`listing-name-${c.id}`}>{c.company_name}</Link>
          <button onClick={() => onFavourite(c)} className={`shrink-0 ${c.favourite ? "text-rose-500" : "text-muted-foreground hover:text-rose-500"}`} title={c.favourite ? "Remove favourite" : "Save as favourite"} data-testid={`fav-btn-${c.id}`}><Heart size={18} weight={c.favourite ? "fill" : "regular"} /></button>
        </div>
        <Stars value={c.rating_avg} count={c.rating_count} size={13} />
        <div className="flex flex-wrap gap-1">
          {c.trades.slice(0, 3).map((t) => <Pill key={t} tone={TONE_CLASSES.none}>{t}</Pill>)}
          {(c.trades.length + (c.custom_trades?.length || 0)) > 3 && <Pill tone={TONE_CLASSES.none}>+{c.trades.length + (c.custom_trades?.length || 0) - 3}</Pill>}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2">{c.bio}</p>
        <div className="text-xs text-muted-foreground space-y-0.5 mt-auto pt-1">
          <p className="truncate"><MapPin size={11} className="inline mr-1" />{c.distance?.miles != null ? <span className="font-medium text-foreground" data-testid={`distance-${c.id}`}>{c.distance.miles} mi {c.distance.kind === "driving" ? "drive" : "away (straight line)"}</span> : null}{c.distance?.miles != null ? " · " : ""}{areaText(c.areas)}</p>
          <p className="truncate"><Clock size={11} className="inline mr-1" />{availText(c.availability)}</p>
          <p className="truncate font-medium text-foreground">{rateText(c.rates)}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap pt-1">
          {away && <span data-testid={`away-pill-${c.id}`}><Pill tone={AWAY_TONE}><Airplane size={11} className="mr-1" /> {away}</Pill></span>}
          {c.insured && <Pill tone={TONE_CLASSES.ok}><SealCheck size={11} className="mr-1" /> Insured</Pill>}
          {c.availability?.emergency_callouts && <Pill tone="text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/30 dark:border-amber-800"><Lightning size={11} className="mr-1" /> 24/7</Pill>}
          {c.jobs_completed > 0 && <span className="text-[11px] text-muted-foreground">{c.jobs_completed} job{c.jobs_completed === 1 ? "" : "s"} via Fixlr</span>}
        </div>
        {c.response_label && <p className="text-[11px] text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1" data-testid={`response-badge-${c.id}`}><ChatCircleDots size={12} /> {c.response_label}</p>}
        <div className="flex items-center gap-2 mt-1">
          <Button asChild size="sm" variant="outline" className="flex-1"><Link to={`/marketplace/contractors/${c.id}`} data-testid={`listing-view-${c.id}`}>View & request quote</Link></Button>
          {onCompare && (
            <label className={`inline-flex items-center gap-1.5 text-xs cursor-pointer select-none ${compareDisabled && !compare ? "opacity-50" : ""}`} title="Compare side by side (up to 3)">
              <input type="checkbox" checked={!!compare} disabled={compareDisabled && !compare} onChange={() => onCompare(c)} data-testid={`compare-check-${c.id}`} /> Compare
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
